import { ref } from 'vue';
import { isAxiosError } from 'axios';
import { useModal } from 'vue-final-modal';
import { useCustomToast, pluralize } from '@/shared/lib';
import { ConfirmDeleteModal } from '@/shared/ui';
import { trainingMatrixApi } from '@/entities/Training';

const CONFIRM_DESCRIPTION = 'A ação não pode ser desfeita.';
const GENERIC_ERROR_MESSAGE = 'Tente novamente.';

function buildConfirmTitle(count: number): string {
  return count === 1
    ? 'Tem certeza que deseja deletar 1 Matriz de treinamento?'
    : `Tem certeza que deseja deletar ${count} Matrizes de treinamento?`;
}

export function useBulkDeleteTrainingMatrixEntries(
  getSelectedIds: () => number[],
  onDeleted: () => Promise<void> | void
) {
  const toast = useCustomToast();
  const isDeleting = ref(false);
  const modalTitle = ref('');

  const { open: openModal, close: closeModal } = useModal({
    component: ConfirmDeleteModal,
    attrs: {
      get title() {
        return modalTitle.value;
      },
      description: CONFIRM_DESCRIPTION,
      onClose: () => closeModal(),
      onConfirm: () => performDelete(),
    },
  });

  function requestDelete(): void {
    modalTitle.value = buildConfirmTitle(getSelectedIds().length);
    openModal();
  }

  async function performDelete(): Promise<void> {
    closeModal();
    isDeleting.value = true;
    const count = getSelectedIds().length;
    try {
      await trainingMatrixApi.deletePositionsEntries(getSelectedIds());
      const matrixWord = pluralize(count, 'Matriz de treinamento', 'Matrizes de treinamento');
      const deletedWord = pluralize(count, 'deletada', 'deletadas');
      toast.showSuccessToast({
        title: 'Sucesso',
        message: `${count} ${matrixWord} ${deletedWord} com sucesso.`,
        type: 'success',
      });
      await onDeleted();
    } catch (error) {
      const serverMessage =
        isAxiosError(error) && typeof error.response?.data?.message === 'string'
          ? error.response.data.message
          : undefined;
      toast.showErrorToast({
        title: `Erro ao deletar ${pluralize(count, 'Matriz de treinamento', 'Matrizes de treinamento')}`,
        message: serverMessage ?? GENERIC_ERROR_MESSAGE,
        type: 'error',
      });
    } finally {
      isDeleting.value = false;
    }
  }

  return {
    isDeleting,
    requestDelete,
  };
}
